import React, { useEffect, useState, useRef } from 'react';
import ThemeToggle from './components/ThemeToggle';
import LanguageToggle from './components/LanguageToggle';
import { useNavigate } from 'react-router-dom';
import { AiOutlineCamera, AiOutlineCopy, AiOutlineDelete } from 'react-icons/ai';
import { fetchUserProfile, useLogout, useOutsideClick, getFirstTwoLetters, getAvatarColor, validatePassword, validateUsername, clearProfileCache, getErrorMessage } from './common';
import { clearClientStorage } from './utils/browserStorage';
import { GlobalStyle, DropdownItem, LogoutButton, MenuButton, DropdownMenu } from './styles/GlobalStyles';
import { authAPI, profileAPI } from './api';
import DevicesModal from './components/workspace/modals/DevicesModal';
import { ensureE2E, subscribeE2EChanged } from './utils/e2e';
import { clearChatPassphrase } from './utils/chatCrypto';
import { useT } from './LocaleContext';
import { 
  ProfileContainer, 
  Title, 
  InfoText, 
  StatusBadge,
  Button,
  ActionButton,
  DestructiveButton,
  ModalOverlay, 
  ModalContainer, 
  ModalTitle,
  ModalText, 
  ModalTextContainer, 
  DeletingSpinner,
  WarningText,
  WarningList,
  WarningItem,
  ModalButton, 
  ButtonContainer,
  ProfileAvatar,
  ProfileAvatarImage,
  AvatarSection,
  AvatarContainer,
  AvatarEditButton,
  AvatarDeleteButton,
  AvatarUploadInput,
  AvatarPreview,
  AvatarPreviewContainer,
  ProfileInfoCard,
  ProfileInfoRow,
  ProfileInfoLabel,
  ProfileInfoValue,
  ProfileActions,
  CopyButton,
  PasswordModalForm,
  PasswordModalInput,
  PasswordModalStatus,
  PasswordModalStatusMessage,
  PasswordRequirements,
  ModalForm,
  ModalFormInput,
  ModalFormStatus,
  ModalFormStatusMessage,
  CopyNotification
} from './styles/ProfilePageStyles';



const ProfilePage = () => {
  const t = useT();
  const [user, setUser] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false); 
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isNameModalOpen, setIsNameModalOpen] = useState(false);
  const [newUserName, setNewUserName] = useState('');
  const [nameMessage, setNameMessage] = useState('');
  const [isNameSuccess, setIsNameSuccess] = useState(false);
  const [isAvatarModalOpen, setIsAvatarModalOpen] = useState(false);
  const [selectedAvatarFile, setSelectedAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [confirmationCode, setConfirmationCode] = useState('');
  const [generatedCode, setGeneratedCode] = useState('');
  const [error, setError] = useState('');
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [passwordMessage, setPasswordMessage] = useState('');
  const [showCopyNotification, setShowCopyNotification] = useState(false);
  const [isDevicesModalOpen, setIsDevicesModalOpen] = useState(false);
  const [e2eReady, setE2eReady] = useState(false);
  const navigate = useNavigate();
  const handleLogout = useLogout();
  const dropdownRef = useRef(null);
  const avatarInputRef = useRef(null);
  const copyButtonRef = useRef(null);

  useEffect(() => {
    const getUserProfile = async () => {
      try {
        const userData = await fetchUserProfile();
        // Проверяем, что данные есть и в правильном формате
        if (userData && typeof userData === 'object' && userData !== null) {
          setUser(userData);
          
          // Загружаем аватар, если он есть
          if (userData.has_avatar) {
            try {
              const avatarResponse = await profileAPI.getAvatar();
              // Check content-type to determine if response is JSON or blob
              const contentType = avatarResponse.headers['content-type'] || '';
              if (contentType.startsWith('image/')) {
                // Image blob response - convert arraybuffer to blob, then to object URL
                const avatarBlob = new Blob([avatarResponse.data], { type: contentType });
                const avatarObjectUrl = URL.createObjectURL(avatarBlob);
                setAvatarUrl(avatarObjectUrl);
              } else if (contentType.startsWith('application/json')) {
                // JSON response means "use default avatar" - parse from arraybuffer
                const decoder = new TextDecoder();
                const jsonText = decoder.decode(avatarResponse.data);
                const jsonData = JSON.parse(jsonText);
                if (jsonData.message === 'use default avatar') {
                  setAvatarUrl(null);
                }
              } else {
                setAvatarUrl(null);
              }
            } catch (error) {
              setAvatarUrl(null);
            }
          } else {
            setAvatarUrl(null);
          }
        } else {
          setUser(null);
        }
      } catch (error) {
        if (error.response && error.response.status === 401) {
          navigate('/login');
        } else {
          // Если ошибка не 401, все равно показываем страницу, но без данных
          setUser(null);
        }
      }
    };
    getUserProfile();
  }, [navigate]);
  
  // Очистка URL при размонтировании компонента
  useEffect(() => {
    return () => {
      if (avatarUrl) {
        URL.revokeObjectURL(avatarUrl);
      }
    };
  }, [avatarUrl]);

  useOutsideClick(dropdownRef, () => setMenuOpen(false));
  const toggleMenu = () => setMenuOpen((prev) => !prev);

  useEffect(() => {
    let cancelled = false;
    clearChatPassphrase();
    const boot = async () => {
      try {
        await ensureE2E();
        if (!cancelled) setE2eReady(true);
      } catch (_) {
        if (!cancelled) setE2eReady(false);
      }
    };
    boot();
    const unsub = subscribeE2EChanged(() => {
      if (!cancelled) setE2eReady(true);
    });
    return () => {
      cancelled = true;
      unsub();
    };
  }, []);

  // Автоматическое исчезновение ошибок через 3 секунды
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (passwordMessage && !isSuccess) {
      const timer = setTimeout(() => {
        setPasswordMessage('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [passwordMessage, isSuccess]);

  const openDeleteModal = () => {
    setGeneratedCode(Math.floor(100000 + Math.random() * 900000).toString());
    setIsModalOpen(true);
  };

  const handleConfirmDelete = async (e) => {
    e?.preventDefault?.();
    if (confirmationCode === generatedCode) {
      try {
        setIsModalOpen(false);
        setIsDeleting(true);
        
        await profileAPI.deleteProfile();
        
        // Небольшая задержка для показа анимации
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        // Куки удаляются бекендом автоматически
        clearClientStorage();
        // Очищаем кеш профиля (clearProfileCache также очищает кеш на уровне API)
        clearProfileCache();
        // Перенаправляем на корневую страницу с полной перезагрузкой
        window.location.href = '/';
      } catch (error) {
        setIsDeleting(false);
        setIsModalOpen(true);
        setError('error deleting account. please try again later');
      }
    } else {
      setError('incorrect confirmation code');
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setConfirmationCode('');
    setError('');
  };

  const openPasswordModal = () => {
    setOldPassword('');
    setNewPassword('');
    setConfirmNewPassword('');
    setPasswordMessage('');
    setIsSuccess(false);
    setIsPasswordModalOpen(true);
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    setPasswordMessage('');
    setIsSuccess(false);
    
    if (!oldPassword || !newPassword || !confirmNewPassword) {
      setPasswordMessage('all fields are required');
      return;
    }

    const passwordValidation = validatePassword(newPassword);
    if (!passwordValidation.valid) {
      setPasswordMessage(passwordValidation.message);
      return;
    }
  
    if (newPassword !== confirmNewPassword) {
      setPasswordMessage('new passwords do not match');
      return;
    }

    try {
      const response = await authAPI.passwordUpdate({
        old_password: oldPassword,
        password: newPassword,
        confirm_password: confirmNewPassword
      });

      if (response.data?.success) {
        setPasswordMessage(t('profile.passwordUpdated'));
        setIsSuccess(true);
        setTimeout(() => setIsPasswordModalOpen(false), 2000);
      } else {
        setPasswordMessage(response.data?.msg || t('common.unknownError'));
      }

    } catch (error) {
      if (error.response) {
        const errorMessage = getErrorMessage(error, t('common.unknownError'));
        setPasswordMessage(errorMessage);
      } else {
        setPasswordMessage(t('common.unknownError'));
      }
      setIsSuccess(false);
    }
  };

  const closePasswordModal = () => {
    setIsPasswordModalOpen(false);
    setIsSuccess(false);
    setPasswordMessage('');
  };

  const openNameModal = () => {
    setNewUserName(user?.user_name || '');
    setNameMessage('');
    setIsNameSuccess(false);
    setIsNameModalOpen(true);
  };

  const closeNameModal = () => {
    setIsNameModalOpen(false);
    setNameMessage('');
    setIsNameSuccess(false);
  };

  const handleNameUpdate = async (e) => {
    e.preventDefault();
    setNameMessage('');
    setIsNameSuccess(false);

    const trimmedName = newUserName.trim();
    if (!trimmedName) {
      setNameMessage('username is required');
      return;
    }

    const usernameValidation = validateUsername(trimmedName);
    if (!usernameValidation.valid) {
      setNameMessage(usernameValidation.message);
      return;
    }

    if (trimmedName === user.user_name) {
      closeNameModal();
      return;
    }

    try {
      const response = await profileAPI.updateUserName(trimmedName);
      const updatedProfile = response.data;

      clearProfileCache();
      setUser((prev) => ({
        ...prev,
        ...updatedProfile,
        user_name: updatedProfile.user_name,
      }));

      window.dispatchEvent(new CustomEvent('profileUpdated'));
      setNameMessage(t('profile.nameUpdated'));
      setIsNameSuccess(true);
      setTimeout(() => closeNameModal(), 1500);
    } catch (error) {
      const errorCode = error.response?.data?.error_code;
      if (errorCode === 2004) {
        setNameMessage(t('profile.usernameTaken'));
      } else {
        setNameMessage(getErrorMessage(error, 'failed to update username'));
      }
      setIsNameSuccess(false);
    }
  };

  const openAvatarModal = () => {
    setError('');
    setIsAvatarModalOpen(true);
    setSelectedAvatarFile(null);
    setAvatarPreview(null);
  };

  const closeAvatarModal = () => {
    setIsAvatarModalOpen(false);
    setSelectedAvatarFile(null);
    setAvatarPreview(null);
    setError('');
    if (avatarInputRef.current) {
      avatarInputRef.current.value = '';
    }
  };

  const handleAvatarFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Проверяем тип файла
      if (!file.type.startsWith('image/')) {
        setError('please select an image file');
        return;
      }
      const maxAvatarBytes = 5 * 1024 * 1024;
      if (file.size > maxAvatarBytes) {
        setError('image size should be less than 5MB');
        return;
      }
      setSelectedAvatarFile(file);
      setError('');
      // Создаем preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAvatarUpload = async () => {
    if (!selectedAvatarFile) {
      setError('please select an image file');
      return;
    }
    
    try {
      setError('');
      const formData = new FormData();
      formData.append('file', selectedAvatarFile);
      
      const response = await profileAPI.uploadAvatar(formData);
      
      if (response.data && response.data.success) {
        // Очищаем кеш аватара и профиля перед загрузкой нового
        profileAPI.clearAvatarCache();
        clearProfileCache(); // Очищаем кеш профиля (включая кеш в common.js), чтобы получить актуальные данные
        
        // Обновляем профиль пользователя
        const userData = await fetchUserProfile();
        setUser(userData);
        
        // Загружаем новый аватар
        if (userData.has_avatar) {
          try {
            // Освобождаем старый URL
            if (avatarUrl) {
              URL.revokeObjectURL(avatarUrl);
            }
            
            // Обходим кеш при загрузке нового аватара
            const avatarResponse = await profileAPI.getAvatar(null, true);
            
            // Check content-type to determine if response is JSON or blob
            const contentType = avatarResponse.headers['content-type'] || '';
            if (contentType.startsWith('image/')) {
              // Image blob response - convert arraybuffer to blob, then to object URL
              const avatarBlob = new Blob([avatarResponse.data], { type: contentType });
              const avatarObjectUrl = URL.createObjectURL(avatarBlob);
              setAvatarUrl(avatarObjectUrl);
            } else if (contentType.startsWith('application/json')) {
              // JSON response means "use default avatar" - parse from arraybuffer
              const decoder = new TextDecoder();
              const jsonText = decoder.decode(avatarResponse.data);
              const jsonData = JSON.parse(jsonText);
              if (jsonData.message === 'use default avatar') {
                setAvatarUrl(null);
              }
            } else {
              setAvatarUrl(null);
            }
          } catch (error) {
            console.error('Error loading avatar after upload:', error);
            setAvatarUrl(null);
          }
        } else {
          setAvatarUrl(null);
        }
        
        // Уведомляем другие компоненты об изменении аватара
        window.dispatchEvent(new CustomEvent('avatarUpdated'));
        
        closeAvatarModal();
      } else {
        console.error('Upload failed:', response.data);
        setError('failed to upload avatar');
      }
    } catch (error) {
      setError(getErrorMessage(error, 'failed to upload avatar'));
    }
  };

  const handleAvatarDelete = async () => {
    try {
      const response = await profileAPI.deleteAvatar();
      
      if (response.data && response.data.success) {
        // Очищаем кеш аватара после удаления
        profileAPI.clearAvatarCache();
        clearProfileCache();
        
        // Освобождаем старый URL
        if (avatarUrl) {
          URL.revokeObjectURL(avatarUrl);
        }
        
        // Мгновенно показываем дефолтный аватар
        setAvatarUrl(null);
        setUser(prev => {
          if (!prev) return prev;
          return { ...prev, has_avatar: false };
        });
        
        // Обновляем профиль пользователя
        const userData = await fetchUserProfile();
        setUser(userData);
        
        // Уведомляем другие компоненты об изменении аватара
        window.dispatchEvent(new CustomEvent('avatarUpdated'));
      } else {
        setError('failed to delete avatar');
      }
    } catch (error) {
      setError(getErrorMessage(error, 'failed to delete avatar'));
    }
  };

  const handleCopyConnectId = async () => {
    if (user && user.connect_id) {
      try {
        await navigator.clipboard.writeText(user.connect_id);
        setShowCopyNotification(true);
        setTimeout(() => {
          setShowCopyNotification(false);
        }, 2000);
      } catch (err) {
        // Fallback для старых браузеров
        const textArea = document.createElement('textarea');
        textArea.value = user.connect_id;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
          setShowCopyNotification(true);
          setTimeout(() => {
            setShowCopyNotification(false);
          }, 2000);
        } catch (err) {
          // Игнорируем ошибки копирования
        }
        document.body.removeChild(textArea);
      }
    }
  };

  if (!user) {
    return (
      <ProfileContainer>
        <GlobalStyle />
        <InfoText>{t('profile.loading')}</InfoText>
      </ProfileContainer>
    );
  }

  return (
    <ProfileContainer>
      <GlobalStyle />
      {user && (
        <>
          <MenuButton onClick={toggleMenu} $open={menuOpen}>
            {user.email ? user.email.slice(0, 2).toUpperCase() : '≡'}
          </MenuButton>
          <DropdownMenu ref={dropdownRef} open={menuOpen}>
            <DropdownItem to="/">{t('nav.home')}</DropdownItem>
            <DropdownItem to="/chats">{t('nav.chats')}</DropdownItem>
            {user.is_admin && <DropdownItem to="/admin">{t('nav.adminPanel')}</DropdownItem>}
            <ThemeToggle />
            <LanguageToggle />
            <LogoutButton onClick={handleLogout}>{t('nav.logout')}</LogoutButton>
          </DropdownMenu>
        </>
      )}
      <Title>{t('profile.title')}</Title>
      <AvatarSection>
        <AvatarContainer>
          {avatarUrl ? (
            <ProfileAvatarImage src={avatarUrl} alt="avatar" />
          ) : user && !user.has_avatar ? (
            <ProfileAvatar color={getAvatarColor(user.user_name || user.email)}>
              {getFirstTwoLetters(user.user_name || user.email)}
            </ProfileAvatar>
          ) : null}
          <AvatarEditButton onClick={openAvatarModal} title={t('profile.changeAvatar')}>
            <AiOutlineCamera />
          </AvatarEditButton>
          {avatarUrl && (
            <AvatarDeleteButton onClick={handleAvatarDelete} title={t('profile.removeAvatar')}>
              <AiOutlineDelete />
            </AvatarDeleteButton>
          )}
        </AvatarContainer>
      </AvatarSection>

      <ProfileInfoCard>
        <ProfileInfoRow>
          <ProfileInfoLabel>{t('common.email')}</ProfileInfoLabel>
          <ProfileInfoValue>{user.email}</ProfileInfoValue>
        </ProfileInfoRow>
        <ProfileInfoRow>
          <ProfileInfoLabel>{t('common.name')}</ProfileInfoLabel>
          <ProfileInfoValue>{user.user_name}</ProfileInfoValue>
        </ProfileInfoRow>
        <ProfileInfoRow>
          <ProfileInfoLabel>{t('profile.connectId')}</ProfileInfoLabel>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
            <ProfileInfoValue>{user.connect_id}</ProfileInfoValue>
            <div style={{ position: 'relative' }}>
              <CopyButton ref={copyButtonRef} onClick={handleCopyConnectId} title={t('profile.copyConnectId')}>
                <AiOutlineCopy />
              </CopyButton>
              <CopyNotification show={showCopyNotification}>{t('profile.copied')}</CopyNotification>
            </div>
          </div>
        </ProfileInfoRow>
        <ProfileInfoRow>
          <ProfileInfoLabel title={t('profile.twoFaTooltip')}>
            {t('profile.twoFa')}
          </ProfileInfoLabel>
          <StatusBadge $enabled={user.is_2fa_enabled}>
            {user.is_2fa_enabled ? t('common.on') : t('common.off')}
          </StatusBadge>
        </ProfileInfoRow>
        <ProfileInfoRow>
          <ProfileInfoLabel title={t('profile.e2eTooltip')}>
            {t('profile.e2e')}
          </ProfileInfoLabel>
          <StatusBadge $enabled={e2eReady}>
            {e2eReady ? t('common.on') : '…'}
          </StatusBadge>
        </ProfileInfoRow>
      </ProfileInfoCard>

      <ProfileActions>
        <Button to="/2fa">
          {user.is_2fa_enabled ? t('profile.manage2fa') : t('profile.setup2fa')}
        </Button>
        <ActionButton onClick={() => setIsDevicesModalOpen(true)}>{t('profile.sessions')}</ActionButton>
        <ActionButton onClick={openNameModal}>{t('profile.changeName')}</ActionButton>
        <ActionButton onClick={openPasswordModal}>{t('profile.changePassword')}</ActionButton>
        <DestructiveButton onClick={openDeleteModal}>{t('profile.deleteAccount')}</DestructiveButton>
      </ProfileActions>

      {isModalOpen && (
        <ModalOverlay>
          <ModalContainer>
            <ModalTextContainer>
              <ModalTitle>{t('profile.deleteTitle')}</ModalTitle>
              <WarningText>
                {t('profile.deleteWarning')}
              </WarningText>
              <WarningList>
                <WarningItem>• {t('profile.deleteChats')}</WarningItem>
                <WarningItem>• {t('profile.deleteSessions')}</WarningItem>
                <WarningItem>• {t('profile.deleteProfile')}</WarningItem>
              </WarningList>
              <ModalText style={{ marginTop: '16px' }}>{t('profile.confirmationCode')}:</ModalText>
              <ModalText><strong>{generatedCode}</strong></ModalText>
            </ModalTextContainer>
            <ModalForm onSubmit={handleConfirmDelete}>
              <ModalFormInput
                type="text"
                value={confirmationCode}
                onChange={(e) => setConfirmationCode(e.target.value.trim())}
                onPaste={(e) => {
                  e.preventDefault();
                  return false;
                }}
                onCopy={(e) => {
                  e.preventDefault();
                  return false;
                }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  return false;
                }}
                onKeyDown={(e) => {
                  if ((e.ctrlKey || e.metaKey) && (e.key === 'v' || e.key === 'V')) {
                    e.preventDefault();
                    return false;
                  }
                }}
                placeholder={t('profile.confirmationCode')}
              />
              <ModalFormStatus>
                <ModalFormStatusMessage success={false}>{error || ''}</ModalFormStatusMessage>
              </ModalFormStatus>
              <ButtonContainer>
                <ModalButton type="button" onClick={closeModal}>{t('common.cancel')}</ModalButton>
                <ModalButton type="submit" $danger>{t('profile.confirmDelete')}</ModalButton>
              </ButtonContainer>
            </ModalForm>
          </ModalContainer>
        </ModalOverlay>
      )}

      {isDeleting && (
        <ModalOverlay>
          <ModalContainer>
            <ModalTextContainer>
              <ModalTitle>{t('profile.deleting')}</ModalTitle>
              <DeletingSpinner />
            </ModalTextContainer>
          </ModalContainer>
        </ModalOverlay>
      )}

      {isNameModalOpen && (
        <ModalOverlay>
          <ModalContainer>
            <ModalTextContainer>
              <ModalTitle>{t('profile.changeName')}</ModalTitle>
              <ModalText>{t('profile.usernameRules')}</ModalText>
            </ModalTextContainer>
            <ModalForm onSubmit={handleNameUpdate}>
              <ModalFormInput
                type="text"
                placeholder={t('profile.username')}
                value={newUserName}
                onChange={(e) => setNewUserName(e.target.value.replace(/[^a-zA-Z0-9_-]/g, ''))}
                autoFocus
                maxLength={100}
                $hasError={!!nameMessage && !isNameSuccess}
              />
              <ModalFormStatus>
                <ModalFormStatusMessage success={isNameSuccess}>
                  {nameMessage || ''}
                </ModalFormStatusMessage>
              </ModalFormStatus>
              <ButtonContainer>
                <ModalButton type="button" onClick={closeNameModal}>{t('common.cancel')}</ModalButton>
                <ModalButton type="submit" primary disabled={!newUserName.trim()}>
                  {t('common.save')}
                </ModalButton>
              </ButtonContainer>
            </ModalForm>
          </ModalContainer>
        </ModalOverlay>
      )}

      {isPasswordModalOpen && (
        <ModalOverlay>
          <ModalContainer>
            <ModalTextContainer>
              <ModalTitle>{t('profile.updatePasswordTitle')}</ModalTitle>
            </ModalTextContainer>
            <PasswordModalForm onSubmit={handlePasswordUpdate}>
              <PasswordModalInput
                type="password"
                placeholder={t('profile.currentPassword')}
                value={oldPassword}
                maxLength={128}
                autoComplete="current-password"
                onChange={(e) => setOldPassword(e.target.value)}
              />
              <PasswordRequirements>
                {t('profile.passwordHint')}
              </PasswordRequirements>
              <PasswordModalInput
                type="password"
                placeholder={t('profile.newPassword')}
                value={newPassword}
                maxLength={128}
                autoComplete="new-password"
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <PasswordModalInput
                type="password"
                placeholder={t('profile.confirmNewPassword')}
                value={confirmNewPassword}
                maxLength={128}
                autoComplete="new-password"
                onChange={(e) => setConfirmNewPassword(e.target.value)}
              />
              <PasswordModalStatus>
                <PasswordModalStatusMessage success={isSuccess}>
                  {passwordMessage || ''}
                </PasswordModalStatusMessage>
              </PasswordModalStatus>
              <ButtonContainer>
                <ModalButton type="button" onClick={closePasswordModal}>{t('common.cancel')}</ModalButton>
                <ModalButton type="submit" primary>{t('profile.updatePassword')}</ModalButton>
              </ButtonContainer>
            </PasswordModalForm>
          </ModalContainer>
        </ModalOverlay>
      )}

      {isAvatarModalOpen && (
        <ModalOverlay>
          <ModalContainer>
            <ModalTextContainer>
              <ModalTitle>{t('profile.uploadAvatar')}</ModalTitle>
              <ModalText>{t('profile.selectImage')}</ModalText>
            </ModalTextContainer>
            <ModalForm
              onSubmit={(e) => {
                e.preventDefault();
                handleAvatarUpload();
              }}
            >
              <AvatarUploadInput
                type="file"
                accept="image/*"
                ref={avatarInputRef}
                onChange={handleAvatarFileChange}
              />
              {avatarPreview && (
                <AvatarPreviewContainer>
                  <AvatarPreview src={avatarPreview} alt="avatar preview" />
                </AvatarPreviewContainer>
              )}
              {error ? (
                <ModalFormStatus>
                  <ModalFormStatusMessage success={false}>{error}</ModalFormStatusMessage>
                </ModalFormStatus>
              ) : null}
              <ButtonContainer>
                <ModalButton type="button" onClick={closeAvatarModal}>{t('common.cancel')}</ModalButton>
                <ModalButton type="submit" primary disabled={!selectedAvatarFile}>
                  {t('profile.uploadAvatar')}
                </ModalButton>
              </ButtonContainer>
            </ModalForm>
          </ModalContainer>
        </ModalOverlay>
      )}

      <DevicesModal
        isOpen={isDevicesModalOpen}
        onClose={() => setIsDevicesModalOpen(false)}
      />

    </ProfileContainer>
  );
};

export default ProfilePage;
